import { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { VERIFY_CUSTOMER_ACCOUNT } from "../../../mutations/VerifyCustomerAccount/verifyCustomerAccount.mutation";
import { AccountContext } from "../../../context/account.context";
import {
  VerifyCustomerAccountResponse,
  VerifyCustomerAccountVariables,
} from "./verify-account.types";

interface VerifyState {
  status: "loading" | "success" | "error";
  message: string;
}

export const useVerifyAccount = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { accountState, setAccountState } = useContext(
    AccountContext
  ) as unknown as {
    accountState: Record<string, unknown>;
    setAccountState: (state: Record<string, unknown>) => void;
  };

  const token = new URLSearchParams(location.search).get("token");

  const [state, setState] = useState<VerifyState>({
    status: "loading",
    message: t("auth.verifyingAccount"),
  });

  const [verifyCustomerAccount] = useMutation<
    VerifyCustomerAccountResponse,
    VerifyCustomerAccountVariables
  >(VERIFY_CUSTOMER_ACCOUNT);

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: t("auth.invalidVerificationLink") });
      return;
    }

    verifyCustomerAccount({ variables: { token } })
      .then((result) => {
        const verifyResult = result?.data?.verifyCustomerAccount;

        if (verifyResult?.__typename === "CurrentUser") {
          setState({ status: "success", message: t("auth.accountVerified") });
          return;
        }

        setState({
          status: "error",
          message: verifyResult?.message ?? t("errors.generic"),
        });
      })
      .catch((err) => {
        setState({
          status: "error",
          message: (err as Error)?.message || t("errors.generic"),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onContinueToLogin = () => {
    setAccountState({ ...accountState, isLoginModalOpen: true });
    history.push("/home");
  };

  return {
    ...state,
    onContinueToLogin,
  };
};
